import { ContextualInfoButtonWithText } from "@/components/buttons/ContextualInfoButton";
import { LineChart, type DataSeries } from "@/components/charts/LineChart";
import AdaptationTag from "@/components/display/AdaptationTag";
import { getBaseThemeName, getHazardColor, getHazardName } from "@/features/branding";
import { HazardPlusTotal } from "@/features/pe_aldd";
import { AnaHazardId } from "@/store/services/facades/types";
import { PeaSummaryResponse, Year } from "@/store/services/facades/types/pea";
import { adaptationConfig, AdaptationType } from "@/types/adaptation";
import { Rcp } from "@/types/rcp";
import { MagnifyingGlassMinusIcon, MagnifyingGlassPlusIcon } from "@phosphor-icons/react";
import { Button, Switch } from "antd";
import classNames from "classnames";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useCollectionState } from "../../context/CollectionContext";
import { CompareResultsMode } from "../../types";
import { formatMetricValue, formatYAxisTick, formatYAxisTickToPercent, getHazardsByScenario, isHazardMetricDataAvailable, MetricKey, multiplierForMetric, unitForMetric } from "./common/utils";
type Props = {
  data: PeaSummaryResponse;
  compareData?: PeaSummaryResponse;
  rcp: Rcp;
  selectedYear: Year;
  hazard: HazardPlusTotal;
  normalized: boolean;
  setNormalized: (normalized: boolean) => void;
  metric: MetricKey;
  metricLabel?: string;
  compareResultsMode?: CompareResultsMode;
  comparisonType?: AdaptationType;
  isPointLevel: boolean;
  showHazardLegend?: boolean;
  canRunAutoAdaptation?: boolean;
  /** Shown in place of the chart when the selected hazard has no data for the selected metric. */
  unavailableMessage?: string;
};
interface HazardLegendProps {
  series: DataSeries[];
  normalized: boolean;
  setNormalized: (normalized: boolean) => void;
  shouldRescale: boolean;
  setShouldRescale: (shouldRescale: boolean) => void;
  hasSelection: boolean;
  isComparison: boolean;
  comparisonType: AdaptationType;
  showHazardLegend: boolean;
  hazard: HazardPlusTotal;
  onHazardToggle: (hazard: AnaHazardId) => void;
}
const COMPARE_SUFFIX = "_compare";
const HazardLegendContent = ({
  series,
  normalized,
  setNormalized,
  shouldRescale,
  setShouldRescale,
  hasSelection,
  isComparison,
  comparisonType,
  showHazardLegend,
  hazard,
  onHazardToggle
}: HazardLegendProps) => {
  const {
    t
  } = useTranslation("terms");
  const isHkma = getBaseThemeName() === "hkma";

  // In comparison mode each hazard has a baseline and a compare series sharing a colour and name,
  // so only the baseline ones are listed.
  const baselineSeries = series.filter(s => !s.key.endsWith(COMPARE_SUFFIX));
  return <div className="mr-[48px] flex flex-col gap-1 pb-2 pl-2 text-xs text-gray-900 [&_.ant-switch-checked>.ant-switch-inner]:!bg-gray-700 [&_.ant-switch]:!bg-gray-200" data-sentry-component="HazardLegendContent" data-sentry-source-file="HazardTimelineChart.tsx">
      <div className="flex h-[24px] items-center gap-4 text-right">
        <div className="flex w-full items-center gap-1 pr-12">
          {isComparison ? <div className="flex flex-1 shrink-0 items-center gap-2 text-gray-500">
              <div className="flex items-center gap-1">
                <svg width="20" height="10">
                  <line x1="0" y1="5" x2="20" y2="5" stroke="currentColor" strokeWidth="2.5" />
                </svg>
                <span>
                  <ContextualInfoButtonWithText id="baselineAdaptation">
                    {t("adaptationTypes.baseline")}
                  </ContextualInfoButtonWithText>
                </span>
              </div>
              <div className="flex items-center gap-1">
                <svg width="20" height="10">
                  <line x1="0" y1="5" x2="20" y2="5" stroke="currentColor" strokeWidth="2.5" strokeDasharray="4 2" />
                </svg>
                <span>
                  <ContextualInfoButtonWithText id={adaptationConfig[comparisonType].contextId}>
                    {t(`adaptationTypes.${comparisonType}`)}
                  </ContextualInfoButtonWithText>
                </span>
              </div>
            </div> : <>
              <span className="shrink-0">Show as % of Total</span>
              <Switch checked={normalized} onChange={val => setNormalized(val)} size="small" />
              {normalized && <span className="ml-2 min-w-0 truncate text-xs text-gray-500">
                  Shows the relative proportion of each hazard to the whole (100%)
                </span>}
            </>}
        </div>
        {(!isComparison ? !normalized && hasSelection : hasSelection) && <Button size="small" type="text" className="h-[24px] gap-0.5 text-xs text-gray-900" onClick={() => setShouldRescale(!shouldRescale)} title={shouldRescale ? "Zoom out to show all hazards" : "Zoom to selected hazard"} icon={shouldRescale ? <MagnifyingGlassMinusIcon size={16} /> : <MagnifyingGlassPlusIcon size={16} />}>
            <span>{shouldRescale ? "Zoom Out" : "Zoom In"}</span>
          </Button>}
      </div>

      {showHazardLegend && !!baselineSeries.length && <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-gray-600">
          {baselineSeries.map(item => {
        const isSelected = hazard !== "total" && item.key === hazard;
        return <button key={item.key} type="button" disabled={isHkma} onClick={() => onHazardToggle(item.key as AnaHazardId)} title={isSelected ? "Show all hazards" : `Highlight ${item.name ?? item.key}`} className={classNames("flex items-center gap-1 rounded transition-opacity", {
          "opacity-30 hover:opacity-100": hasSelection && !isSelected && !isHkma,
          "font-medium text-gray-900": isSelected,
          "cursor-pointer": !isHkma
        })}>
                <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{
            backgroundColor: item.color
          }} />
                <span>{item.name ?? item.key}</span>
              </button>;
      })}
        </div>}
    </div>;
};
export const HazardTimelineChart = ({
  data,
  compareData,
  rcp,
  selectedYear,
  hazard,
  normalized,
  setNormalized,
  metric,
  metricLabel,
  compareResultsMode,
  comparisonType = "enhanced_resilience",
  isPointLevel,
  showHazardLegend = false,
  canRunAutoAdaptation,
  unavailableMessage
}: Props) => {
  const {
    setYear,
    setHazard
  } = useCollectionState();
  const [shouldRescale, setShouldRescale] = useState(true);
  const {
    t: tHazardBreakdown
  } = useTranslation("tools", {
    keyPrefix: "portfolios.portfolioLevel.hazardBreakdown"
  });
  const isComparison = compareResultsMode === "compare";
  const tagType = canRunAutoAdaptation ? isPointLevel ? comparisonType : compareResultsMode : undefined;
  const buildHazardTimelineChartData = (data: PeaSummaryResponse, compareData: PeaSummaryResponse | undefined, rcp: Rcp, metric: MetricKey, normalized: boolean, compareResultsMode?: CompareResultsMode): {
    chartData: Array<Record<string, number | Year>>;
    series: DataSeries[];
  } => {
    const multiplier = multiplierForMetric(metric);
    const byHazard = getHazardsByScenario(data, rcp);
    const hazards = byHazard.map(r => r.hazard).sort((a, b) => String(a).localeCompare(String(b)));
    const years = Object.keys((byHazard?.[0] as unknown as Record<string, unknown>)?.[metric] ?? {}).map(y => Number(y)).sort((a, b) => a - b);
    const getValue = (row: (typeof byHazard)[number], year: number) => Number(((row as unknown as Record<string, unknown>)[metric] as Record<string, unknown>)?.[year] ?? 0) * multiplier;
    if (compareResultsMode === "compare" && compareData?.length) {
      const compareByHazard = getHazardsByScenario(compareData, rcp);
      const chartData = years.map(year => {
        const row: Record<string, number | Year> = {
          year
        };
        byHazard.forEach(h => {
          row[h.hazard] = getValue(h, year);
        });
        compareByHazard.forEach(h => {
          row[`${h.hazard}${COMPARE_SUFFIX}`] = getValue(h, year);
        });
        return row;
      });
      const series: DataSeries[] = hazards.flatMap(h => {
        const color = getHazardColor(h as AnaHazardId);
        const name = getHazardName(h as AnaHazardId);
        return [{
          key: `${h}${COMPARE_SUFFIX}`,
          color,
          name,
          strokeDasharray: "4 2"
        }, {
          key: h,
          color,
          name,
          alwaysSolid: true
        }];
      });
      return {
        chartData,
        series
      };
    }
    const chartData = years.map(year => {
      const row: Record<string, number | Year> = {
        year
      };
      let sum = 0;
      byHazard.forEach(h => {
        const v = getValue(h, year);
        row[h.hazard] = v;
        sum += v;
      });
      if (normalized && sum > 0) {
        byHazard.forEach(h => {
          const val = row[h.hazard] as number ?? 0;
          row[h.hazard] = val / sum * 100;
        });
      }
      return row;
    });
    const series: DataSeries[] = hazards.map(h => ({
      key: h,
      color: getHazardColor(h as AnaHazardId),
      name: getHazardName(h as AnaHazardId),
      strokeDasharray: isPointLevel || compareResultsMode === "baseline" ? undefined : "4,2",
      alwaysSolid: isPointLevel || compareResultsMode === "baseline"
    }));
    return {
      chartData,
      series
    };
  };
  const hazardDataAvailable = isHazardMetricDataAvailable(hazard, metric);
  const {
    chartData,
    series
  } = useMemo(() => hazardDataAvailable ? buildHazardTimelineChartData(data, compareData, rcp, metric, normalized, compareResultsMode) : {
    chartData: [],
    series: []
  }, [data, compareData, rcp, metric, normalized, hazard, shouldRescale, hazardDataAvailable]);
  const yAxisDomain = useMemo(() => {
    const canRescale = (isComparison ? true : !normalized) && shouldRescale && hazard !== "total" && chartData.length > 0;
    if (!canRescale) return undefined;

    // Find maximum value for the selected hazard across all years
    let maxValue = 0;
    const keysToCheck = isComparison ? [hazard, `${hazard}${COMPARE_SUFFIX}`] : [hazard];
    for (const dataPoint of chartData) {
      for (const key of keysToCheck) {
        const value = dataPoint[key];
        if (typeof value === "number" && isFinite(value) && value > maxValue) {
          maxValue = value;
        }
      }
    }

    // If no valid data found, use auto scaling
    if (maxValue <= 0 || !isFinite(maxValue)) {
      return undefined;
    }

    // 5% padding
    const upperBound = maxValue * 1.05;
    return [0, upperBound] as [number, number];
  }, [isComparison, normalized, shouldRescale, hazard, chartData]);
  const hasSelection = hazard !== "total";
  const selectedDataKeys = hazard === "total" ? [] : isComparison ? [hazard, `${hazard}${COMPARE_SUFFIX}`] : [hazard];
  const filteredSeries = hasSelection ? series.filter(s => selectedDataKeys.includes(s.key)) : series;

  // Same toggle behaviour as the hazard breakdown bars: clicking the selected hazard clears the selection.
  const handleHazardToggle = (clicked: AnaHazardId) => setHazard(hazard === clicked ? "total" : clicked);

  // Normalised shares stack to 100, so the ticks read as percent whatever the metric.
  const isStacked = !isComparison && normalized;
  return <LineChart formatTick={value => isStacked ? formatYAxisTickToPercent(value) : formatYAxisTick(metric, value)} chartData={chartData} series={filteredSeries} formatValue={value => isStacked ? formatMetricValue("%", value) : formatMetricValue(unitForMetric(metric), value, unitForMetric(metric) === "$" ? 2 : 4)} metricLabel={metricLabel} selectedDataKeys={selectedDataKeys} selectedYear={selectedYear} onYearChange={setYear} legendContent={props => <HazardLegendContent {...props}
  // The legend lists every hazard even while one is highlighted, so it stays the way to switch/clear.
  series={series} hazard={hazard} onHazardToggle={handleHazardToggle} normalized={normalized} setNormalized={setNormalized} shouldRescale={shouldRescale} setShouldRescale={setShouldRescale} hasSelection={hasSelection} isComparison={isComparison} comparisonType={comparisonType} showHazardLegend={showHazardLegend} />} columnKeySuffix={isComparison ? COMPARE_SUFFIX : undefined} columnLabels={isComparison ? [<AdaptationTag key={"baseline"} type="baseline" />, <AdaptationTag key={comparisonType} type={comparisonType} />] : undefined} tag={tagType && <AdaptationTag type={tagType} />} emptyMessage={!hazardDataAvailable && unavailableMessage ? unavailableMessage : tHazardBreakdown("unavailableMessage")} showArea={isComparison ? hazard !== "total" : !normalized && hazard !== "total"} showReferenceLine={true} stacked={isStacked} yAxisDomain={yAxisDomain} isAnimationActive={true} data-sentry-element="LineChart" data-sentry-component="HazardTimelineChart" data-sentry-source-file="HazardTimelineChart.tsx" />;
};